
import { useGlobalDiscoveryFacets } from "@entities/global-discovery/hooks/useGlobalDiscoveryFacets";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { Select } from "@shared/ui";
import { useEffect, useMemo, useState } from "react";
import styles from "./TagsFilter.module.scss";

interface TagsFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
  platformId?: string;
  options?: { label: string; value: string }[];
  loading?: boolean;
}

export function TagsFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
  platformId: platformIdProp,
  options: optionsProp,
  loading: loadingProp,
}: TagsFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;
  const platformId = platformIdProp ?? globalStore.platformId;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch facet options from backend
  const { facet, isFetching: isFacetsFetching } = useGlobalDiscoveryFacets({
    facetName: filter.facetName,
    body: {},
    query: debouncedQuery,
    work_platform_id: platformId,
    enabled: !optionsProp && !!platformId,
  });

  const isFetching = optionsProp ? !!loadingProp : isFacetsFetching;

  // Convert facet data to Select options, including selected values not in facets
  const options = useMemo(() => {
    if (optionsProp) {
      if (searchQuery) {
        return optionsProp.filter((opt) =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      return optionsProp;
    }

    const facetOptions = (facet || []).map((item) => ({
      label: item.name,
      value: item.value,
    }));

    const facetValues = new Set(facetOptions.map((opt) => opt.value));
    const selectedValues = Array.isArray(selectedFilters[filter.facetName])
      ? (selectedFilters[filter.facetName] as string[])
      : [];

    // Add selected values that are not in facet results (for custom tags)
    const missingOptions: Array<{ label: string; value: string }> = [];
    selectedValues.forEach((value) => {
      if (!facetValues.has(value)) {
        missingOptions.push({ label: value, value });
      }
    });

    return [...facetOptions, ...missingOptions];
  }, [facet, selectedFilters, filter.facetName, optionsProp, searchQuery]);

  // Get current selected values
  const currentValue = useMemo(() => {
    const value = selectedFilters[filter.facetName];
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }, [selectedFilters, filter.facetName]);

  const handleChange = (values: string[]) => {
    // For tags, value and filterTags are the same (array of strings)
    // Users can add custom tags, so we store the tag value itself as the label
    setSelectedFilter((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filter.facetName]: values,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: values, // For tags, filterTags = values
        },
      };
      return newFilters;
    });
  };

  const handleSelect = () => {
    // Clear search query immediately when an option is selected
    setSearchQuery("");
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    // Clear search query when dropdown closes
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <div className={styles.tagsFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <Select
        className={styles.select}
        size="small"
        mode="tags"
        placeholder={filter.placeHolder || "Add tags"}
        options={options}
        value={currentValue}
        onChange={handleChange}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        searchValue={searchQuery || undefined}
        showSearch
        loading={isFetching}
        withFooterActions
        allowClear={false}
        filterOption={false}
      />
    </div>
  );
}
